import { reachabilityMessage } from "@/api/errors";
import { type LoginResponse, type TenantOption, login, loginSelect } from "@/api/login";
import { useAuth } from "@/app/auth";
import { useOnline } from "@/app/online";
import { rememberSecret } from "@/app/relogin";
import { armPinOffer } from "@/app/vault";
import { useMutation } from "@tanstack/react-query";
import { LoaderCircle, WifiOff } from "lucide-react";
import { type FormEvent, useState } from "react";

const OFFLINE_MESSAGE =
  "Sin señal: iniciar sesión necesita conexión porque la contraseña la valida el servidor. Buscá señal y volvé a intentar.";

export function LoginScreen() {
  const { signIn } = useAuth();
  const online = useOnline();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [disambiguationToken, setDisambiguationToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  function apply(data: LoginResponse) {
    if (data.status === "authenticated" && data.access_token) {
      signIn(data.access_token);
      return;
    }
    if (data.status === "disambiguation_required" && data.disambiguation_token) {
      setDisambiguationToken(data.disambiguation_token);
      setTenants(data.tenants ?? []);
      return;
    }
    setError("El servidor no devolvió una sesión válida.");
  }

  function remember(tenantId: string | null) {
    rememberSecret({ kind: "password", identifier: identifier.trim(), password, tenantId });
    armPinOffer();
  }

  const credentials = useMutation({
    mutationFn: () => login(identifier.trim(), password),
    onSuccess: (data: LoginResponse) => {
      if (data.status === "authenticated") {
        remember(null);
      }
      apply(data);
    },
    onError: (cause: unknown) => setError(messageOf(cause)),
  });

  const tenantChoice = useMutation({
    mutationFn: (tenantId: string) => loginSelect(disambiguationToken ?? "", tenantId),
    onSuccess: (data: LoginResponse, tenantId: string) => {
      if (data.status === "authenticated") {
        remember(tenantId);
      }
      apply(data);
    },
    onError: (cause: unknown) => setError(messageOf(cause)),
  });

  const busy = credentials.isPending || tenantChoice.isPending;
  const canSubmit = online && !busy && identifier.trim() !== "" && password !== "";

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!canSubmit) {
      return;
    }
    credentials.mutate();
  }

  return (
    <main className="login">
      <div className="login__card">
        <p className="t-eyebrow">CRM de campo</p>
        <h1 className="t-h1">Entrar</h1>

        {online ? null : (
          <p className="notice notice--offline">
            <WifiOff size={16} aria-hidden="true" />
            {OFFLINE_MESSAGE}
          </p>
        )}

        {disambiguationToken === null ? (
          <form className="login__form" onSubmit={onSubmit} noValidate>
            <label className="field" htmlFor="identifier">
              <span className="field__label">Usuario, email o teléfono</span>
              <input
                id="identifier"
                name="identifier"
                className="field__input"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
              />
            </label>

            <label className="field" htmlFor="password">
              <span className="field__label">Contraseña</span>
              <input
                id="password"
                name="password"
                className="field__input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error ? (
              <p className="notice notice--error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="btn btn--primary btn--block" disabled={!canSubmit}>
              {busy ? <LoaderCircle size={18} aria-hidden="true" /> : null}
              {busy ? "Entrando…" : "Entrar"}
            </button>
          </form>
        ) : (
          <div className="login__tenants">
            <p className="t-body">Tu usuario trabaja en más de un concesionario. Elegí uno:</p>
            {error ? (
              <p className="notice notice--error" role="alert">
                {error}
              </p>
            ) : null}
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                type="button"
                className="btn btn--block"
                disabled={busy || !online}
                onClick={() => {
                  setError(null);
                  tenantChoice.mutate(tenant.id);
                }}
              >
                {tenant.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function messageOf(cause: unknown): string {
  return reachabilityMessage(cause, OFFLINE_MESSAGE);
}
